import React, { Component } from 'react'
import { Bar, BarChart, CartesianGrid, Cell, Label, Legend, Tooltip, XAxis, YAxis, ResponsiveContainer } from 'recharts'
import { isEmpty, isEqual, sortBy } from 'lodash'
import PropTypes from 'prop-types'

import {
  fetchBerthsOccupationFromBackend,
  fetchKpiDischargeSpeed,
  fetchKpiLoadStationOccupation,
  fetchLaybyBerthOccupationData
} from '../../../js/rest'
import { colors, occupationPeriod, processLaybyOccupationData, transformOccupationData } from '../../../utils/kpiUtils'
import SpinnerOverlay from '../../spinnerOverlay/SpinnerOverlay'
import { handleFetchError } from '../../../js/sentry'
import { chartMargins, legendStyling } from '../../../utils/renderUtils'
import { APP_TERMINAL_IDS, kpiColorPalette } from '../../../js/constants'
import ProductDischargeTooltip from './productDischargeTooltip/ProductDischargeTooltip'
import OccupancyBarChart from '../kpiCharts/occupancyBarChart/OccupancyBarChart'
import KpiBarchartTooltip from '../kpiBarchartTooltip/KpiBarchartTooltip'
import EmptyPlaceholderChart from '../kpiCharts/emptyPlaceholderChart/EmptyPlaceholderChart'

export default class KpiPerformance extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fetchingData: false,
      stationsOccupation: undefined,
      dischargeSpeeds: undefined,
      berthsOccupation: undefined,
      laybyBerthOccupation: undefined
    }
  }

  static propTypes = {
    authToken: PropTypes.string,
    customFetch: PropTypes.func.isRequired,
    terminalId: PropTypes.string.isRequired,
    selection: PropTypes.object.isRequired,
    stationColors: PropTypes.object.isRequired,
    productLoadSpeeds: PropTypes.object.isRequired,
    productColors: PropTypes.object.isRequired
  }

  async getLoadstationsOccupation() {
    try {
      const occupationDataList = await fetchKpiLoadStationOccupation(
        this.props.terminalId,
        this.props.selection.startDate,
        this.props.selection.stopDate,
        this.props.authToken,
        this.props.customFetch,
        occupationPeriod.month
      )

      //data from backend nested under the terminal name
      const newObject = Object.keys(occupationDataList).reduce((a, b) => {
        if (b === 'external') return a
        occupationDataList[b].forEach(({ date, locations }) => {
          if (a[date]) {
            // merge the locations together if the date is already merged
            const currentLocations = a[date].locations
            currentLocations.concat(locations)
            a[date].locations = [...currentLocations, ...locations]
          } else {
            a[date] = { date, locations }
          }
        })

        return a
      }, {})
      return transformOccupationData(Object.values(newObject), 'locations')
    } catch (err) {
      handleFetchError(err)
      return null
    }
  }

  async getLaybyBerthOccupation() {
    // only relevant for yara
    if (this.props.terminalId !== APP_TERMINAL_IDS.yara) {
      return null
    }

    try {
      const laybyData = await fetchLaybyBerthOccupationData(
        this.props.terminalId,
        this.props.selection.startDate,
        this.props.selection.stopDate,
        this.props.authToken,
        this.props.customFetch,
        occupationPeriod.month
      )

      return processLaybyOccupationData(laybyData)
    } catch (err) {
      handleFetchError(err)
      return null
    }
  }

  async getBerthOccupation() {
    try {
      const occupationDataList = await fetchBerthsOccupationFromBackend(
        this.props.terminalId,
        this.props.selection.startDate,
        this.props.selection.stopDate,
        this.props.authToken,
        this.props.customFetch,
        occupationPeriod.month
      )

      //data from backend nested under the terminal name
      const newObject = Object.keys(occupationDataList).reduce((a, b) => {
        if (b === 'external') return a
        occupationDataList[b].forEach(({ date, berthOccupation }) => {
          if (a[date]) {
            // merge the locations together if the date is already merged
            const currentLocations = a[date].berthOccupation
            currentLocations.concat(berthOccupation)
            a[date].berthOccupation = [...currentLocations, ...berthOccupation]
          } else {
            a[date] = { date, berthOccupation }
          }
        })

        return a
      }, {})

      return transformOccupationData(Object.values(newObject), 'berthOccupation')
    } catch (err) {
      handleFetchError(err)
      return null
    }
  }

  async getAverageDischargeSpeed() {
    try {
      const dischargeSpeed = await fetchKpiDischargeSpeed(
        this.props.terminalId,
        this.props.selection.startDate,
        this.props.selection.stopDate,
        this.props.authToken,
        this.props.customFetch
      )

      return sortBy(dischargeSpeed, 'product').map(({ product, loading, unloading }) => ({
        product,
        // product average speed can either be for loading or unloading, but not both
        averageSpeed: Math.round(loading.avg || unloading.avg),
        defaultSpeed: this.props.productLoadSpeeds[product]
      }))
    } catch (err) {
      handleFetchError(err)
      return null
    }
  }

  fetchAllData() {
    this.setState({
      fetchingData: true
    })

    Promise.all([
      this.getLoadstationsOccupation(),
      this.getAverageDischargeSpeed(),
      this.getBerthOccupation(),
      this.getLaybyBerthOccupation()
    ]).then(([stationsOccupation, dischargeSpeeds, berthsOccupation, laybyBerthOccupation]) => {
      this.setState({
        stationsOccupation,
        dischargeSpeeds,
        berthsOccupation,
        laybyBerthOccupation,
        fetchingData: false
      })
    })
  }

  componentDidUpdate(prevProps) {
    const hasSelectionChanged = !isEqual(this.props.selection, prevProps.selection)
    const hasSelectedTerminalChanged = this.props.terminalId !== prevProps.terminalId

    if (hasSelectionChanged || hasSelectedTerminalChanged) {
      this.fetchAllData()
    }
  }

  componentDidMount() {
    if (this.props.selection.startDate && this.props.selection.stopDate) {
      this.fetchAllData()
    }
  }

  render() {
    const { dischargeSpeeds, stationsOccupation, berthsOccupation, laybyBerthOccupation } = this.state

    const canRenderstationsOccupationChart = stationsOccupation && !isEmpty(stationsOccupation)
    const canRenderBerthOccupationChart = berthsOccupation && !isEmpty(berthsOccupation)
    const canRenderDischargeSpeedChart = !isEmpty(dischargeSpeeds)
    const canRenderLaybyBerthChart = !isEmpty(laybyBerthOccupation)

    return (
      <div className="kpi-chart-container">
        <div className="chart-group">
          {canRenderstationsOccupationChart ? (
            <div className="chart-item full-width">
              <OccupancyBarChart
                data={stationsOccupation.data}
                itemColors={this.props.stationColors}
                itemList={stationsOccupation.stations}
                yAxisLabel="Occupancy percentage"
                xAxisLabel="Average occupancy per load station"
                aspect={4.0 / 2.0}
              />
            </div>
          ) : (
            <div className="chart-item">
              <EmptyPlaceholderChart
                yAxisLabel="Occupancy percentage"
                xAxisLabel="Average occupancy per load station"
              />
            </div>
          )}

          {canRenderBerthOccupationChart ? (
            <div className="chart-item">
              <OccupancyBarChart
                data={berthsOccupation.data}
                itemColors={berthsOccupation.stations.reduce(
                  (acc, val, idx) => ({ ...acc, [val]: kpiColorPalette[idx] }),
                  {}
                )}
                itemList={berthsOccupation.stations}
                yAxisLabel="Occupancy percentage"
                xAxisLabel="Average occupancy per berth"
              />
            </div>
          ) : (
            <div className="chart-item">
              <EmptyPlaceholderChart yAxisLabel="Occupancy percentage" xAxisLabel="Average occupancy per berth" />
            </div>
          )}

          {this.props.terminalId === APP_TERMINAL_IDS.yara && (
            <div className="chart-item">
              {canRenderLaybyBerthChart ? (
                <ResponsiveContainer width="100%" aspect={4.0 / 3.0}>
                  <BarChart data={laybyBerthOccupation} margin={chartMargins}>
                    <CartesianGrid stroke="#f5f5f5" />
                    <XAxis dataKey="date" interval={0} angle={-45} height={80} textAnchor="end">
                      <Label value="Lay-by berth occupation" offset={-3} position="insideBottom" />
                    </XAxis>
                    <YAxis>
                      <Label value="Hours" position="insideLeft" angle={-90} style={{ textAnchor: 'middle' }} />
                    </YAxis>

                    <Tooltip content={<KpiBarchartTooltip />} />

                    <Bar stackId="0" name="Yara" dataKey="yara" fill="#2fa3e3" maxBarSize={80} />
                    <Bar stackId="0" name="Non-Yara" dataKey="nonYara" fill="#89B0AE" maxBarSize={80} />
                    <Legend verticalAlign="top" wrapperStyle={legendStyling} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyPlaceholderChart yAxisLabel="Hours" xAxisLabel="Lay-by berth occupation" />
              )}
            </div>
          )}

          <div className="chart-item">
            {canRenderDischargeSpeedChart ? (
              <ResponsiveContainer width="100%" aspect={4.0 / 3.0}>
                <BarChart data={dischargeSpeeds} margin={chartMargins} layout="vertical">
                  <pattern
                    id="pattern-stripe"
                    width="4"
                    height="4"
                    patternUnits="userSpaceOnUse"
                    patternTransform="rotate(45)"
                  >
                    <rect width="2" height="4" transform="translate(0,0)" fill={colors.grey} />
                  </pattern>

                  <mask id="mask-stripe">
                    <rect x="0" y="0" width="100%" height="100%" fill="url(#pattern-stripe)" />
                  </mask>
                  <CartesianGrid stroke="#f5f5f5" />
                  <YAxis type="category" dataKey="product" width={135} />
                  <XAxis type="number" label={{ value: 'Discharge speed (mt/hr)', position: 'bottom', offset: -5 }} />

                  <Tooltip content={<ProductDischargeTooltip productColors={this.props.productColors} />} />

                  <Bar dataKey="averageSpeed" name="Average speed">
                    {dischargeSpeeds.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={this.props.productColors[entry.product]} />
                    ))}
                  </Bar>

                  <Bar dataKey="defaultSpeed" name="Default speed">
                    {dischargeSpeeds.map((entry, index) => (
                      <Cell
                        mask="url(#mask-stripe)"
                        key={`cell-${index}`}
                        fill={this.props.productColors[entry.product]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyPlaceholderChart yAxisLabel="product" xAxisLabel="Discharge speed (mt/hr)" />
            )}
          </div>
        </div>

        <SpinnerOverlay loading={this.state.fetchingData} />
      </div>
    )
  }
}
